/* eslint-disable import/no-cycle */
// TODO: migrate to BlockDividerButton (src/components/block-divider-button/BlockDividerButton.tsx)
import { PropsWithChildren } from 'react';
import { Button, Divider, Dropdown, IconPlusCircle, List, Tooltip, useDropdownState } from '@screentone/core';
import clonedeep from 'lodash.clonedeep';
import { v4 as uuidv4 } from 'uuid';

import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { useGroup } from 'contexts/group/useGroup';
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import {
  AvailableLayoutModule,
  ModuleContainerUnion,
  MultiZoneModuleAttributes,
  PageModule,
  StructureGroupConfig
} from 'data/generated/graphql';
import { convertPageModuleItemsToDataModelEntities } from 'utils/allesseh';
import { MULTIZONE_MODULE } from 'utils/constants';
import { findTreatmentSetting, getTreatmentType } from 'utils/modules';
import styles from './AddModuleButton.module.scss';

interface AddModuleButtonProps {
  addableUiModules?: AvailableLayoutModule[];
  nextModuleIndex: number;
  parentHierarchyId?: string;
  /** When provided, adds a module into the named group instead of at the root level. */
  groupId?: string;
}

export const AddModuleButton = ({
  addableUiModules,
  nextModuleIndex,
  parentHierarchyId,
  groupId,
  children = 'Add a Module Here'
}: PropsWithChildren<AddModuleButtonProps>) => {
  const { open: isAddModuleDropdownOpen, setOpen: setIsAddModuleDropdownOpen, componentRef } = useDropdownState();
  const { insertEntity, getAllModuleCounts, getGroupConfig } = useDataModelContext();
  const { group: currentGroup } = useGroup();
  const { pageTypeSetting, currentLayout, structureTreatmentTypes } = usePageTypeSettingsContext();

  // ─── Group path ────────────────────────────────────────────────────────────
  // When groupId is provided we read allowed types + max from whichever shape is
  // present (structure new shape, or hardcoded SETTINGS old shape) and add
  // the new module into that group. Completely separate from the root-level path.
  if (groupId) {
    const structureGroupConfig = getGroupConfig(groupId) as StructureGroupConfig | undefined;
    // SETTINGS_ONLY(structure-migration): settingsGroupConfig is the old-shape fallback; remove once all pubs are migrated
    const settingsGroupConfig = currentLayout?.groupConfigs?.find((gc) => gc.id === groupId);
    // END SETTINGS_ONLY(structure-migration)

    const allowedTypes = structureGroupConfig?.allowedTreatmentTypes ?? settingsGroupConfig?.allowedTreatmentTypes;
    const groupMax = structureGroupConfig?.max ?? settingsGroupConfig?.maximumAllowed ?? Infinity;
    const atMax = currentGroup.collection.length >= groupMax;

    if (!allowedTypes || atMax) return null;

    // SETTINGS_ONLY(structure-migration): treatmentTypeSettings is empty for structure pubs; fall back to structureTreatmentTypes.
    const settingsTreatmentTypes = pageTypeSetting?.treatmentTypeSettings ?? [];
    const treatmentTypeSettings = settingsTreatmentTypes.length > 0 ? settingsTreatmentTypes : structureTreatmentTypes;
    const groupModuleOptions = allowedTypes
      .map((tt) => findTreatmentSetting(treatmentTypeSettings, tt))
      .filter((t): t is NonNullable<typeof t> => t != null);

    if (groupModuleOptions.length === 0) return null;

    const handleAddGroupModule = (option: string) => {
      const treatmentSetting = groupModuleOptions.find((t) => t.label === option);
      const treatmentType = treatmentSetting?.treatmentTypeKey ?? treatmentSetting?.treatmentType ?? '';
      const defaultModule = treatmentSetting?.module as PageModule;
      const collection = convertPageModuleItemsToDataModelEntities(defaultModule);

      const usedSlotRefs = new Set(
        currentGroup.collection
          .map((item) => (item as ModuleContainerUnion).slotRef)
          .filter((slotRef): slotRef is string => !!slotRef)
      );
      let slotIndex = 0;
      while (usedSlotRefs.has(`${treatmentType}-${slotIndex}`)) slotIndex += 1;

      const newModule = {
        type: 'Module' as const,
        uuid: uuidv4(),
        attributes: { pageModule: defaultModule, brand: treatmentSetting?.brand } as MultiZoneModuleAttributes,
        collection,
        groupId,
        isGroupEnabled: true,
        slotRef: `${treatmentType}-${slotIndex}`
      } as unknown as ModuleContainerUnion;

      if (treatmentSetting?.subtype === MULTIZONE_MODULE) {
        newModule.subtype = MULTIZONE_MODULE;
        (newModule.attributes as MultiZoneModuleAttributes).zones = treatmentSetting.zones;
      }

      insertEntity(`0-0-${nextModuleIndex}`, newModule);
      setIsAddModuleDropdownOpen(false);
    };

    return (
      <div className={styles.blockDivider}>
        <div className={styles.blockDividerContent}>
          <Divider className={styles.blockDividerDivider} />
          <Dropdown
            arrow="top"
            position="center"
            margin={{ horizontal: 'xs' }}
            componentRef={componentRef}
            open={isAddModuleDropdownOpen}
            onToggle={() => setIsAddModuleDropdownOpen(!isAddModuleDropdownOpen)}
          >
            <Dropdown.Trigger>
              <Tooltip>
                <Tooltip.Content position={nextModuleIndex === 0 ? 'bottom' : 'top'}>{children}</Tooltip.Content>
                <Tooltip.Trigger>
                  <Button
                    tertiary
                    icon={IconPlusCircle}
                    onClick={() => setIsAddModuleDropdownOpen(!isAddModuleDropdownOpen)}
                    data-testid="add-module-button"
                  />
                </Tooltip.Trigger>
              </Tooltip>
            </Dropdown.Trigger>
            <Dropdown.Content
              className={styles.blockDividerDropdownContent}
              arrow="top"
              margin={{ top: 'sm' }}
              padding={{ all: 'md' }}
            >
              <List itemMargin={{ vertical: 'xs' }}>
                {groupModuleOptions.map((t) => (
                  <List.Item key={t.label}>
                    <Button
                      fullWidth
                      tertiary
                      className={styles.addableUiModuleOption}
                      onClick={() => handleAddGroupModule(t.label)}
                      data-testid="page-add-module-title-button"
                    >
                      {t.label}
                    </Button>
                  </List.Item>
                ))}
              </List>
            </Dropdown.Content>
          </Dropdown>
          <Divider className={styles.blockDividerDivider} />
        </div>
      </div>
    );
  }
  // ─── End group path ────────────────────────────────────────────────────────

  // Root-level module addition (original logic unchanged)
  const moduleCounts = getAllModuleCounts();
  // SETTINGS_ONLY(structure-migration): treatmentTypeSettings comes from hardcoded SETTINGS; not populated for structure pubs
  const treatmentTypeSettings = pageTypeSetting?.treatmentTypeSettings;
  // END SETTINGS_ONLY(structure-migration)

  const availableModulesAndLabels = currentLayout?.availableTreatmentTypes
    ?.map((treatmentType) => findTreatmentSetting(treatmentTypeSettings ?? [], treatmentType))
    .filter(Boolean);

  const filteredAvailableModules = availableModulesAndLabels?.filter((moduleLabel) => {
    const layoutModule = (currentLayout?.availableLayoutModules ?? []).find((layoutMod) => {
      const treatmentType = getTreatmentType(layoutMod.defaultModule);
      return treatmentType === moduleLabel?.treatmentType;
    });

    if (!layoutModule || !moduleLabel) return true;

    const currentCount = moduleCounts[moduleLabel.treatmentType] || 0;
    return currentCount < (layoutModule.maximumAllowed ?? Infinity);
  });

  if (!addableUiModules || (addableUiModules.length === 0 && !currentLayout?.availableTreatmentTypes?.length)) {
    return null;
  }

  const addModuleOptions = filteredAvailableModules?.length
    ? filteredAvailableModules.map((t) => t?.label)
    : addableUiModules.map((module) => module.defaultModule.uiModuleType);

  const handleAddModule = (index: number, option?: string) => {
    if (addableUiModules.length === 0 && !currentLayout?.availableTreatmentTypes?.length) {
      return;
    }

    const treatmentSetting = filteredAvailableModules?.find((t) => t?.label === option);

    const moduleSubType = treatmentSetting?.subtype;

    const defaultModule = option
      ? (treatmentSetting?.module as PageModule)
      : clonedeep(addableUiModules[index].defaultModule);

    const collection = convertPageModuleItemsToDataModelEntities(defaultModule);

    const attributes = {
      pageModule: defaultModule,
      brand: treatmentSetting?.brand
    } as MultiZoneModuleAttributes;

    const newModule: ModuleContainerUnion = {
      type: 'Module',
      uuid: uuidv4(),
      attributes,
      collection
    };

    if (moduleSubType === MULTIZONE_MODULE) {
      newModule.subtype = MULTIZONE_MODULE;
      newModule.attributes.zones = treatmentSetting?.zones;
    }

    if (parentHierarchyId) {
      insertEntity(`${parentHierarchyId}-${nextModuleIndex}`, newModule);
    } else {
      // defaults to original behavior if parentHierarchyId is missing
      insertEntity(`0-0-${nextModuleIndex}`, newModule);
    }

    setIsAddModuleDropdownOpen(false);
  };

  const handleAddModuleDropdownOpenToggle = () => {
    setIsAddModuleDropdownOpen(!isAddModuleDropdownOpen);
  };

  return (
    <div className={styles.blockDivider}>
      <div className={styles.blockDividerContent}>
        <Divider className={styles.blockDividerDivider} />
        <Dropdown
          arrow="top"
          position="center"
          margin={{ horizontal: 'xs' }}
          componentRef={componentRef}
          open={isAddModuleDropdownOpen}
          onToggle={handleAddModuleDropdownOpenToggle}
        >
          <Dropdown.Trigger>
            <Tooltip>
              <Tooltip.Content position={nextModuleIndex === 0 ? 'bottom' : 'top'}>Add a Module Here</Tooltip.Content>
              <Tooltip.Trigger>
                <Button
                  tertiary
                  icon={IconPlusCircle}
                  onClick={handleAddModuleDropdownOpenToggle}
                  data-testid="add-module-button"
                />
              </Tooltip.Trigger>
            </Tooltip>
          </Dropdown.Trigger>
          <Dropdown.Content
            className={styles.blockDividerDropdownContent}
            arrow="top"
            margin={{ top: 'sm' }}
            padding={{ all: 'md' }}
          >
            <List itemMargin={{ vertical: 'xs' }}>
              {addModuleOptions.map((option, index) => (
                <List.Item key={option}>
                  <Button
                    fullWidth
                    tertiary
                    className={styles.addableUiModuleOption}
                    onClick={() => handleAddModule(index, option ?? undefined)}
                    data-testid="page-add-module-title-button"
                  >
                    {option}
                  </Button>
                </List.Item>
              ))}
            </List>
          </Dropdown.Content>
        </Dropdown>
        <Divider className={styles.blockDividerDivider} />
      </div>
    </div>
  );
};
